/**
 * @fileoverview Composes Register page state, effects, handlers, and validation.
 * @module pages/register/useRegisterForm
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { apiMessageOrGeneric } from "../../utils/genericErrorMessages.js";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { getCountryCodeOptions } from "../../data/selectOptions.js";
import {
  getReferencedDataOptionById,
  getOptionValue,
} from "../../utils/common.js";
import { useFieldValidation } from "../../hooks/useFieldValidation";
import { fetchSectors } from "../../logic/register/index.js";

import {
  HTTP_CONFLICT,
  NO_OPTIONS_I18N_KEY,
  REGISTER_SUCCESS_REDIRECT_MS,
  getNotSingleSelectWidth,
  getSingleSelectWidth,
} from "./registerFormHelpers.js";
import {
  computePasswordValidation,
  computeStep1FieldValidation,
  computeStep2Valid,
  computeStep3Valid,
} from "./registerValidation.js";
import {
  createNotSingleSelectStyles,
  createSingleSelectStyles,
} from "./registerSelectStyles.js";
import { REGISTER_INITIAL_VALUES } from "./registerInitialValues.js";
import useRegisterScreenSize from "./useRegisterScreenSize.js";
import useRegisterReferenceData from "./useRegisterReferenceData.js";

const useRegisterForm = () => {
  const { t } = useTranslation();
  const countryCodeOptions = getCountryCodeOptions(t);
  const navigate = useNavigate();
  const [stepNumber, setStepNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [values, setValues] = useState(REGISTER_INITIAL_VALUES);

  const { isTablet, isMediumMobile, isSmallMobile } = useRegisterScreenSize();

  const selectSectorRef = useRef(null);
  const selectSubsectorRef = useRef(null);
  const selectBranchRef = useRef(null);

  const [sectors, setSectors] = useState([]);
  const [subSectors, setSubsectors] = useState([]);
  const [branches, setBranches] = useState([]);

  const referenceData = useRegisterReferenceData({ setValues, setLoading });

  const { handleFieldChange, getErrorMessage, getFieldValidationState } =
    useFieldValidation({ RFC: "" });

  const registerUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}auth/register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(values),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        if (response.status === HTTP_CONFLICT) {
          setError("RFC_ALREADY_IN_USE");
        } else {
          setError("REGISTER_ERROR");
        }
        throw new Error(apiMessageOrGeneric(data?.message));
      }

      setTimeout(() => navigate("/login"), REGISTER_SUCCESS_REDIRECT_MS);
    } catch (err) {
      logger.error("Error registering user:", { err: sanitizeError(err) });
    } finally {
      setLoading(false);
    }
  }, [navigate, values]);

  const getSafeSelectedOption = useCallback((options, value) => {
    if (!Array.isArray(options)) {
      logger.warn("Options is not an array:", { data: options });
      return null;
    }
    return getReferencedDataOptionById(options, getOptionValue(value));
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const noOptionsMessage = useCallback(() => t(NO_OPTIONS_I18N_KEY), [t]);

  const handleCountryCodeChange = useCallback((selectedOption) => {
    setValues((prev) => ({ ...prev, codigoPais: selectedOption.value }));
  }, []);

  const handlePhoneChange = useCallback(
    (event) => handleInputChange("telefonoDeOficina", event.target.value),
    [handleInputChange],
  );

  const handleNamedInputChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      if (name) {
        handleInputChange(name, value);
      }
    },
    [handleInputChange],
  );

  const handleRfcChange = useCallback(
    (event) => {
      const value = event.target.value;
      handleInputChange("RFC", value);
      handleFieldChange("RFC", value);
    },
    [handleInputChange, handleFieldChange],
  );

  const handleForeignerToggle = useCallback((event) => {
    setValues((prev) => ({ ...prev, esExtranjero: event.target.checked }));
  }, []);

  const handleClickShowPassword = useCallback(() => {
    setValues((prev) => ({ ...prev, showPassword: !prev.showPassword }));
  }, []);

  const handleClickShowConfirmPassword = useCallback(() => {
    setValues((prev) => ({
      ...prev,
      showConfirmPassword: !prev.showConfirmPassword,
    }));
  }, []);

  const handleNavigateToLogin = useCallback(() => navigate("/login"), [navigate]);

  const formatOptionNombreLabel = useCallback(
    (option) => <span>{option.nombre}</span>,
    [],
  );

  const formatOptionCodigoLabel = useCallback(
    (option) => <span>{option.codigo}</span>,
    [],
  );

  const handleSectorChange = useCallback((selectedOption) => {
    if (!selectedOption) {
      return;
    }
    setValues((prev) => ({
      ...prev,
      sectorEconomico: selectedOption._id,
      subSectorSCIAN: null,
      ramaSCIAN: null,
    }));
    selectSubsectorRef.current?.clearValue();
    selectBranchRef.current?.clearValue();
    setSubsectors(selectedOption.subSectores ?? []);
    setBranches([]);
  }, []);

  const handleSubsectorChange = useCallback((selectedOption) => {
    if (!selectedOption) {
      return;
    }
    setValues((prev) => ({
      ...prev,
      subSectorSCIAN: selectedOption._id,
      ramaSCIAN: null,
    }));
    selectBranchRef.current?.clearValue();
    setBranches(selectedOption.ramas ?? []);
  }, []);

  const handleBranchChange = useCallback((selectedOption) => {
    if (!selectedOption) {
      return;
    }
    setValues((prev) => ({ ...prev, ramaSCIAN: selectedOption._id }));
  }, []);

  const passwordValidation = useMemo(
    () =>
      computePasswordValidation(
        values.password || "",
        values.confirmPassword || "",
      ),
    [values.password, values.confirmPassword],
  );

  const fieldValidation = useMemo(
    () => computeStep1FieldValidation(values, passwordValidation),
    [values, passwordValidation],
  );

  const isStep1Valid = useMemo(
    () => fieldValidation.isValid && passwordValidation.isValid,
    [fieldValidation.isValid, passwordValidation.isValid],
  );

  const isStep2Valid = useMemo(
    () => computeStep2Valid(values),
    [
      values.RFC,
      values.razonSocial,
      values.nombreComercial,
      values.sectorEconomico,
      values.taxId,
      values.esExtranjero,
    ],
  );

  const isStep3Valid = useMemo(
    () => computeStep3Valid(values),
    [
      values.calle,
      values.numeroExterior,
      values.numeroInterior,
      values.pais,
      values.estado,
      values.municipio,
      values.colonia,
      values.esExtranjero,
    ],
  );

  const handleNextFromStep1 = useCallback(() => {
    if (isStep1Valid) {
      setStepNumber((prev) => prev + 1);
    }
  }, [isStep1Valid]);

  const handleNextFromStep2 = useCallback(() => {
    if (isStep2Valid) {
      setStepNumber((prev) => prev + 1);
    }
  }, [isStep2Valid]);

  const handlePreviousFromStep2 = useCallback(() => {
    setStepNumber((prev) => prev - 1);
  }, []);

  const handlePreviousFromStep3 = useCallback(() => {
    setStepNumber((prev) => prev - 1);
  }, []);

  const handleCreateAccount = useCallback(() => {
    if (isStep3Valid && !loading) {
      registerUser();
    }
  }, [isStep3Valid, loading, registerUser]);

  useEffect(() => {
    const fetchData = async () => {
      const fetchedSectors = await fetchSectors(navigate);
      setSectors(fetchedSectors);
    };
    fetchData();
  }, [navigate]);

  const notSingleSelectWidth = useMemo(
    () => getNotSingleSelectWidth(isTablet, isMediumMobile, isSmallMobile),
    [isTablet, isMediumMobile, isSmallMobile],
  );

  const singleSelectWidth = useMemo(
    () => getSingleSelectWidth(isTablet, isMediumMobile, isSmallMobile),
    [isTablet, isMediumMobile, isSmallMobile],
  );

  const notSingleSelectStyles = useMemo(
    () => createNotSingleSelectStyles(notSingleSelectWidth),
    [notSingleSelectWidth],
  );

  const subsectorSelectStyles = useMemo(
    () =>
      createNotSingleSelectStyles(notSingleSelectWidth, { marginTop: "7px" }),
    [notSingleSelectWidth],
  );

  const singleSelectStyles = useMemo(
    () => createSingleSelectStyles(singleSelectWidth),
    [singleSelectWidth],
  );

  return {
    t,
    stepNumber,
    loading,
    error,
    isTablet,
    isMediumMobile,
    isSmallMobile,
    values,
    countryCodeOptions,
    sectors,
    subSectors,
    branches,
    ...referenceData,
    selectSectorRef,
    selectSubsectorRef,
    selectBranchRef,
    passwordValidation,
    isStep1Valid,
    isStep2Valid,
    isStep3Valid,
    notSingleSelectStyles,
    subsectorSelectStyles,
    singleSelectStyles,
    getSafeSelectedOption,
    getErrorMessage,
    getFieldValidationState,
    noOptionsMessage,
    formatOptionNombreLabel,
    formatOptionCodigoLabel,
    handleCountryCodeChange,
    handlePhoneChange,
    handleNamedInputChange,
    handleRfcChange,
    handleForeignerToggle,
    handleClickShowPassword,
    handleClickShowConfirmPassword,
    handleNavigateToLogin,
    handleSectorChange,
    handleSubsectorChange,
    handleBranchChange,
    handleNextFromStep1,
    handleNextFromStep2,
    handlePreviousFromStep2,
    handlePreviousFromStep3,
    handleCreateAccount,
  };
};

export default useRegisterForm;
